// Mock content for Herbert Project Partners (HPP) website.
// This drives the frontend-only build. Replace with backend data later.

export const brand = {
  name: 'Herbert Project Partners',
  short: 'HPP',
  logoLight: '/hpp-logo-light.png',
  logoDark: '/hpp-logo-dark.gif',
  logoTransparent: '/hpp-logo-transparent.png',
  positioning:
    'Herbert Project Partners helps organisations turn AI ambition into governed, practical enterprise capability; connecting technology, evidence and human accountability to deliver sustainable business value.',
  primaryCta: 'Discuss Your AI Requirements',
  tonePrinciple:
    'Clear enough to understand. Credible enough to trust. Practical enough to implement.',
  legal: {
    copyright: '© 2026 Herbert Project Partners',
    entity:
      'Herbert Project Partners is a registered business name of Chantelle Herbert.',
    abn: 'ABN 49 243 050 611',
    rights: 'All rights reserved.',
    location: 'Australia',
  },
};

export const nav = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'EIOM', to: '/eiom' },
  { label: 'Services', to: '/services' },
  { label: 'Contact', to: '/contact' },
];

export const heroStats = [
  { value: '20+', label: 'Years enterprise experience' },
  { value: '360°', label: 'Enterprise Intelligence View' },
  { value: '100%', label: 'Human-accountable decisions' },
];

// Problems HPP solves
export const problems = [
  {
    title: 'AI ambition without a governed path',
    body: 'Leadership expects results from AI, but there is no structured, defensible way to move from idea to capability.',
  },
  {
    title: 'Fragmented tools, unclear evidence',
    body: 'Pilots multiply, data is scattered, and decisions cannot be traced back to reliable, auditable evidence.',
  },
  {
    title: 'Risk, compliance and accountability gaps',
    body: 'In regulated environments, unmanaged AI creates exposure. Responsibility for material decisions is not clearly assigned.',
  },
  {
    title: 'Capability that does not last',
    body: 'Point solutions solve today’s problem but leave no reusable operating model, so value erodes over time.',
  },
];

// Approach steps: problem -> approach -> value -> evidence -> next step
export const approach = [
  {
    step: '01',
    title: 'Understand',
    body: 'We start with your business problem, decisions and constraints - not the technology.',
  },
  {
    step: '02',
    title: 'Design',
    body: 'Together we design governed, interoperable capability grounded in evidence and clear accountability.',
  },
  {
    step: '03',
    title: 'Deliver',
    body: 'We implement with proportionate controls, working alongside your teams and delivery partners.',
  },
  {
    step: '04',
    title: 'Sustain',
    body: 'We embed an operating model so capability remains auditable, measurable and durable.',
  },
];

export const whyHpp = [
  {
    title: 'Governance-led',
    body: 'Structure, controls and accountability are designed in from the start - not bolted on afterwards.',
  },
  {
    title: 'Evidence over hype',
    body: 'Every recommendation is grounded in provenance, measurement and commercial reality.',
  },
  {
    title: 'Interoperable by design',
    body: 'Capability connects to your existing systems, data and future CRM and automation.',
  },
  {
    title: 'Human accountability',
    body: 'Responsibility for material decisions remains clearly assigned to people.',
  },
];

// EIOM capability areas
export const eiomPrinciples = [
  { title: 'Governance', body: 'Clear ownership, controls and decision rights across the AI lifecycle.' },
  { title: 'Interoperability', body: 'Capability that connects systems, data and teams rather than creating silos.' },
  { title: 'Evidence', body: 'Decisions grounded in verifiable, well-sourced information.' },
  { title: 'Auditability', body: 'A traceable record from data and inputs through to material outcomes.' },
  { title: 'Human accountability', body: 'People remain responsible and in control of consequential decisions.' },
];

export const eiomOutcomes = [
  'Faster, more defensible decisions',
  'Reduced AI, data and compliance risk',
  'Reusable capability instead of one-off pilots',
  'Clear line of sight from evidence to outcome',
  'Confidence for executives, boards and regulators',
];

export const eiomUseCases = [
  {
    title: 'AI readiness & operating model',
    body: 'Establish the governance, roles and structure needed before scaling AI across the organisation.',
  },
  {
    title: 'Decision intelligence',
    body: 'Bring evidence, analytics and accountability together to strengthen material business decisions.',
  },
  {
    title: 'Governed automation',
    body: 'Introduce AI-enabled automation with proportionate controls and clear human oversight.',
  },
];

export const services = [
  {
    name: 'AI Strategy & Readiness',
    tag: 'Advisory',
    body: 'Assess where you are, define where AI creates value, and set a governed path to get there.',
    points: ['Opportunity & risk assessment', 'Operating model design', 'Executive alignment'],
  },
  {
    name: 'Enterprise Intelligence Design',
    tag: 'EIOM',
    body: 'Design governed, interoperable intelligence capability using Herbert Project Partners’ Enterprise Intelligence Operating Model.',
    points: ['Capability blueprint', 'Governance & controls', 'Evidence & auditability'],
  },
  {
    name: 'Analytics & Decision Support',
    tag: 'Delivery',
    body: 'Turn data into decisions with predictive modelling, segmentation and experimentation.',
    points: ['Predictive modelling', 'Customer segmentation', 'Experimentation'],
  },
  {
    name: 'Responsible AI Enablement',
    tag: 'Governance',
    body: 'Embed transparency, oversight and risk-based controls so AI stays accountable as it scales.',
    points: ['Responsible-AI framework', 'Risk-based controls', 'Ongoing evaluation'],
  },
];

export const engagement = [
  { title: 'Discovery', body: 'A focused conversation to understand your challenge, goals and constraints.' },
  { title: 'Design partnership', body: 'We work with you to shape governed capability and a clear plan.' },
  { title: 'Delivery & partners', body: 'Founder-led delivery, engaging specialist collaborators where required.' },
];

export const insights = [
  {
    category: 'Governance',
    title: 'Why AI needs an operating model, not just a pilot',
    excerpt:
      'Pilots prove possibility. Operating models create durable, governed capability that survives leadership and technology change.',
    read: '5 min read',
    date: 'Feb 2026',
  },
  {
    category: 'Evidence',
    title: 'Evidence provenance: the quiet foundation of trustworthy AI',
    excerpt:
      'When decisions can be traced to reliable sources, organisations move faster with less risk. Provenance is a commercial advantage.',
    read: '6 min read',
    date: 'Jan 2026',
  },
  {
    category: 'Responsible AI',
    title: 'Keeping humans accountable as automation scales',
    excerpt:
      'Responsible AI is not about slowing down. It is about designing systems where responsibility for material decisions stays with people.',
    read: '4 min read',
    date: 'Jan 2026',
  },
  {
    category: 'Strategy',
    title: 'From AI ambition to enterprise capability',
    excerpt:
      'A practical view on moving beyond experimentation to governed, interoperable capability that delivers sustainable value.',
    read: '7 min read',
    date: 'Dec 2025',
  },
];

export const caseExamples = [
  {
    sector: 'PropTech',
    title: 'Predictive modelling for operational optimisation',
    body: 'Anonymised example: designed a governed predictive approach to improve operational decisions and reduce manual effort.',
  },
  {
    sector: 'SaaS',
    title: 'Customer segmentation & experimentation',
    body: 'Anonymised example: structured segmentation and experimentation to inform data-driven growth decisions.',
  },
  {
    sector: 'Community & multicultural',
    title: 'Data-informed program development',
    body: 'Anonymised example: supported strategic funding proposals and program design with structured, evidence-led analysis.',
  },
];

export const founder = {
  name: 'Chantelle Herbert',
  role: 'Founder & Director',
  photo: '/chantelle.jpg',
  bio: [
    'Chantelle Herbert is the Founder and Director of Herbert Project Partners, bringing more than 15+ years’ experience managing enterprise relationships across SaaS, PropTech, media and regulated industries.',
    'Her work connects strategic partnerships, customer insight and data-informed decision-making to deliver practical commercial outcomes. Chantelle’s project experience includes predictive modelling, customer segmentation, experimentation and AI-enabled operational optimisation across corporate and community environments.',
    'She has also supported multicultural and Not for Profit organisations through strategic funding proposals and data-informed program development. Through Herbert Project Partners, Chantelle helps organisations apply analytics and emerging AI technologies to strengthen decisions, operations and sustainable growth.',
  ],
  model:
    'Herbert Project Partners operates through a founder-led model, engaging specialist collaborators and delivery partners where required.',
};

export const commitments = [
  {
    title: 'Security commitment',
    body: 'Herbert Project Partners applies proportionate security and data-governance controls to the information entrusted to us. We collect only information required for a defined business purpose, restrict access appropriately and assess the security and data-handling practices of the technology providers used in our work. We do not knowingly place confidential client information into public AI tools without appropriate authorisation and safeguards.',
  },
  {
    title: 'Responsible-AI commitment',
    body: 'Herbert Project Partners supports AI that is governed, transparent and accountable. Our approach prioritises human oversight, evidence provenance, privacy, security, risk-based controls and ongoing evaluation. We design AI-enabled systems so that responsibility for material decisions remains clearly assigned to people.',
  },
];

export const buyerRoles = [
  'Executives & board members',
  'Heads of Strategy & Transformation',
  'Data, Analytics & AI leaders',
  'Risk, Governance & Compliance leaders',
  'Operations & Delivery leaders',
];

export const industries = [
  'SaaS & Technology', 'PropTech', 'Media', 'Financial Services', 'Government & Public Sector',
  'Healthcare', 'Community & Not-for-profit', 'Other',
];

export const serviceOptions = [
  'AI Strategy & Readiness',
  'Enterprise Intelligence Design (EIOM)',
  'Analytics & Decision Support',
  'Responsible AI Enablement',
  'Not sure yet',
];

export const regions = ['Australia', 'New Zealand', 'Asia Pacific', 'Europe', 'North America', 'Other'];
export const orgTypes = ['Enterprise', 'SME', 'Government', 'Not-for-profit', 'Startup', 'Other'];
export const orgSizes = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
export const hearAbout = ['Referral', 'LinkedIn', 'Search', 'Event', 'Article', 'Other'];
